import { createContext, useContext, useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { getAllAdobeIoProduct, getAllAEMAdobeIO, getAllAEMAdobeIOFolders, getAllAtAudienceConfigRule, getAllContentfulIntegration, getAllGithubIntegration, getAllRoles, getAllTealiumProduct, getAppData, getNewWorkspaceData, getUserProfile } from '../utils/APIs';
import { useNavigate } from 'react-router-dom';
import { pathExistsInObject } from '../utils/helper';
import { useContentfulContext } from './ContentfulContext';
import { decryptPermBundle, parseJWTPayload } from '../utils/permDecrypt';


const UserContext = createContext({});

export const UserContextProvider = ({ children }) => {
    const { isAuthenticated, user, getAccessTokenSilently, logout } = useAuth0();
    const { enums } = useContentfulContext();
    const [loading, setLoading] = useState(true)
    const navigate = useNavigate()

    const [roles, setRoles] = useState([]);
    const [appData, setAppData] = useState({});
    const [userData, setUserData] = useState({
        organizations: [],
        selectedOrganization: null,
        atAudienceConfigRule: [],
        token: "",
        adobeIoIntegrations: [],
        contentfulIntegrations: [],
        githubIntegrations: [],
        tealiumIntegrations: [],
        workspaces: [],
        workspacesMap: {},
        aemAdobeIOs: [],
        defaultAdobeIO: "",
        defaultContentful: "",
        defaultGithub: "",
        defaultTealiumKey: "",
        defaultAEMAdobeIO: "",
        defaultAEMFolder: null
    })

    useEffect(() => {
        if (isAuthenticated) {
            getAccessToken()
        }
    }, [isAuthenticated])
    useEffect(() => {
        if (userData?.token && isAuthenticated) {
            fetchUserAndRoles();
        }
    }, [userData?.token])

    useEffect(() => {
        const savedOrg = localStorage.getItem('selectedOrganization');
        if (savedOrg) {
            try {
                const org = JSON.parse(savedOrg);
                setUserData((prev) => ({ ...prev, selectedOrganization: org }));
            } catch (e) { /* ignore */ }
        }
    }, []);

    const getAccessToken = async () => {
        try {
            setLoading(true)
            const token = await getAccessTokenSilently();
            setUserData((prev) => ({ ...prev, token }))
        } catch (error) {
            setLoading(false);
            logout({
                logoutParams: { returnTo: enums?.LOGOUT_REDIRECT_URL || window.location.origin },
            });
        }
    }

    const fetchUserAndRoles = async () => {
        try {
            setLoading(true);
            await Promise.all([getUserProfileData(), getRoles()]);
        } finally {
            setLoading(false);
        }
    };

    const getRoles = async () => {
        const result = await getAllRoles(userData.token);
        if (result?.data?.enc_roles) {
            const { sub: jwtSub, iat } = parseJWTPayload(userData.token);
            try {
                const decryptedRoles = decryptPermBundle(result.data.enc_roles, jwtSub, iat);
                setRoles(decryptedRoles);
            } catch (e) {
                console.error("Failed to decrypt roles array", e);
                setRoles([]);
            }
        } else {
            setRoles(result.data || []);
        }
    }

    const getUserProfileData = async () => {
        try {
            if (!userData.token) return;

            let savedOrg = null;
            try {
                savedOrg = JSON.parse(localStorage.getItem('selectedOrganization'));
            } catch (e) { /* ignore */ }

            const result = await getUserProfile(userData.token, user.sub);
            const profileData = result.data;

            if (!profileData?.organizations) {
                navigate('/dashboard');
                setUserData((prev) => ({ ...prev, ...profileData }));
                return;
            }

            const allOrgs = profileData.organizations || [];

            const accessibleOrgs = allOrgs.filter(org => !org.isBlocked);
            if (accessibleOrgs.length === 0) {
                console.warn("User has no accessible organizations.");
                navigate('/access-denied', { replace: true });
                return;
            }

            let selectedOrg = null;
            if (savedOrg && accessibleOrgs.some(org => org.org_token === savedOrg.org_token)) {
                selectedOrg = accessibleOrgs.find(org => org.org_token === savedOrg.org_token);
            } else {
                selectedOrg = accessibleOrgs.find(
                    org => org.name === profileData.user_metadata?.default_organization
                );
                if (!selectedOrg) {
                    selectedOrg = accessibleOrgs[0];
                }
            }

            const [atAudienceConfigRule, integrations] = await Promise.all([
                getAllAtAudienceConfigRule(userData.token, selectedOrg.org_token),
                getAllIntegrations(selectedOrg)
            ]);

            if (selectedOrg) {
                localStorage.setItem('selectedOrganization', JSON.stringify(selectedOrg));
            }
            setUserData((prev) => ({
                ...prev,
                ...profileData,
                organizations: allOrgs,
                user_metadata: {
                    ...profileData?.user_metadata,
                    default_organization: selectedOrg?.name,
                    organization_name: selectedOrg?.name
                },
                selectedOrganization: selectedOrg,
                atAudienceConfigRule: (atAudienceConfigRule && atAudienceConfigRule.data) ? atAudienceConfigRule.data : [],
                ...integrations
            }));

            // navigate('/dashboard');
        } catch (err) {
            console.log('err', err);
        }
    };


    const updateSelectedOrgData = async () => {
        try {
            const result = await getUserProfile(userData.token, user.sub)
            let selectedOrg = result.data.organizations.find((x) => x.name === userData.selectedOrganization.name);
            setUserData((prev) => ({
                ...prev,
                selectedOrganization: selectedOrg
            }))
        } catch (error) {
            console.log('error in updateSelectedOrgData: ', error);
        }
    }

    const updateDefaultAEMFolder = async (folder) => {
        try {
            setUserData((prev) => ({
                ...prev,
                defaultAEMFolder: folder
            }))
        } catch (error) {
            console.log('error in updateDefaultAemFolder: ', error);
        }
    }

    const getAllAppData = async () => {
        try {
            const res = await getAppData(userData?.token, userData?.selectedOrganization?.org_token);
            setAppData(res.data)
        } catch (error) {
            // console.log('error', error)
        }
    }

    const getAllIntegrations = async (selectedOrg) => {
        try {
            const integrationResults = await Promise.allSettled([
                getAllAdobeIoProduct(userData.token, selectedOrg.org_token),
                getAllTealiumProduct(userData.token, selectedOrg.org_token),
                getAllAEMAdobeIO(userData.token, selectedOrg.org_token),
                getAllContentfulIntegration(userData.token, selectedOrg.org_token),
                getAllGithubIntegration(userData.token, selectedOrg.org_token)
            ]);

            const [adobeIo, tealium, aemAdobeIO, contentful, github] = integrationResults.map((result) =>
                result.status === "fulfilled" ? result.value.data : []
            );

            const defaultAEMAdobeIO = aemAdobeIO?.find((x) => x?.default);

            let defaultAEMFolder = null;
            if (defaultAEMAdobeIO) {
                try {
                    const folders = await getAllAEMAdobeIOFolders(
                        userData.token,
                        selectedOrg.org_token,
                        defaultAEMAdobeIO._id
                    );
                    if (folders?.data?.length > 0) {
                        const firstFolder = folders.data[0];
                        defaultAEMFolder = firstFolder
                    }

                } catch (folderError) {
                    console.error("Failed to fetch folders for default AEM Adobe IO:", folderError);
                }
            }

            const data = {
                adobeIoIntegrations: adobeIo,
                contentfulIntegrations: contentful,
                defaultContentful: contentful?.find((x) => x?.default),
                githubIntegrations: github,
                defaultGithub: github?.find((x) => x?.default),
                defaultAdobeIO: adobeIo?.find((x) => x?.default),
                tealiumIntegrations: tealium,
                defaultTealiumKey: tealium?.find((x) => x?.default),
                aemAdobeIOs: aemAdobeIO,
                defaultAEMAdobeIO: defaultAEMAdobeIO,
                defaultAEMFolder: defaultAEMFolder
            }
            return data
        } catch (error) {
            console.log("error in getAllIntegrations", getAllIntegrations)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (userData?.defaultAdobeIO)
            getWorkspaces();
    }, [userData?.defaultAdobeIO])

    const getWorkspaces = async () => {
        try {
            const workspaces = await getNewWorkspaceData(userData.token, userData?.selectedOrganization.org_token, userData?.defaultAdobeIO.tenant_name)
            const workspaceMap = {};

            workspaces?.data?.forEach((ws) => {
                if (ws.workspace_id) {
                    workspaceMap[ws.workspace_id] = ws.workspace_name || ws.workspace_id;
                }
            });

            setUserData((prev) => ({
                ...prev,
                workspaces: workspaces.data,
                workspacesMap: workspaceMap,
                defaultWorkspace: workspaces.data?.find((x) => x?.default),
            }))
        } catch (error) {
            console.log("error in getAllIntegrations", getAllIntegrations)
        }
    }

    const reFetchATAudienceConfigRules = async (organization = userData.selectedOrganization?.org_token) => {
        try {
            const atAudienceConfigRule = await getAllAtAudienceConfigRule(userData.token, organization)
            setUserData((prev) => ({
                ...prev,
                atAudienceConfigRule: (atAudienceConfigRule && atAudienceConfigRule.data) ? atAudienceConfigRule.data : [],
            }))
        } catch (err) {
            console.log('err', err);
        }
    }

    const updateAdobeIoIntegrations = async (updateDefault) => {
        const adobeIo = await getAllAdobeIoProduct(userData.token, userData?.selectedOrganization.org_token)
        setUserData((prev) => ({
            ...prev,
            adobeIoIntegrations: adobeIo.data,
            defaultAdobeIO: (updateDefault || !prev?.defaultAdobeIO) ? adobeIo.data?.find((x) => x?.default) : prev?.defaultAdobeIO,
        }))
    }

    const updateContentfulIntegrations = async (updateDefault) => {
        const integrations = await getAllContentfulIntegration(userData.token, userData?.selectedOrganization.org_token)
        setUserData((prev) => ({
            ...prev,
            contentfulIntegrations: integrations.data,
            defaultContentful: (updateDefault || !prev?.defaultContentful) ? integrations.data?.find((x) => x?.default) : prev?.defaultContentful,
        }))
    }

    const updateGithubIntegrations = async (updateDefault) => {
        const integrations = await getAllGithubIntegration(userData.token, userData?.selectedOrganization.org_token)
        setUserData((prev) => ({
            ...prev,
            githubIntegrations: integrations.data,
            defaultGithub: (updateDefault || !prev?.defaultGithub) ? integrations.data?.find((x) => x?.default) : prev?.defaultGithub,
        }))
    }

    const updateTealiumIntegrations = async (updateDefault) => {
        const tealiums = await getAllTealiumProduct(userData.token, userData?.selectedOrganization.org_token)
        setUserData((prev) => ({
            ...prev,
            tealiumIntegrations: tealiums.data,
            defaultTealiumKey: (updateDefault || !prev?.defaultTealiumKey) ? tealiums.data?.find((x) => x?.default) : prev?.defaultTealiumKey,
        }))
    }

    const updateWorkspaces = async (updateDefault) => {
        const workspaces = await getNewWorkspaceData(userData.token, userData?.selectedOrganization.org_token, userData?.defaultAdobeIO.tenant_name)
        const workspaceMap = {};

        workspaces?.data?.forEach((ws) => {
            if (ws.workspace_id) {
                workspaceMap[ws.workspace_id] = ws.workspace_name || ws.workspace_id;
            }
        });
        setUserData((prev) => ({
            ...prev,
            workspaces: workspaces.data,
            workspacesMap: workspaceMap,
            defaultWorkspace: (updateDefault || !prev?.defaultWorkspace) ? workspaces.data?.find((x) => x?.default) : prev?.defaultWorkspace,
        }))
    }

    const updateAEMAdobeIOIntegrations = async (updateDefault) => {
        const aemAdobes = await getAllAEMAdobeIO(userData.token, userData?.selectedOrganization.org_token)
        setUserData((prev) => ({
            ...prev,
            aemAdobeIOs: aemAdobes.data,
            defaultAEMAdobeIO: (updateDefault || prev?.defaultAEMAdobeIO) ? aemAdobes.data?.find((x) => x?.default) : prev?.defaultAEMAdobeIO,
        }))
    }

    const updateDefaultAdobeIO = async (adobeIo) => {
        setUserData((prev) => ({
            ...prev,
            defaultAdobeIO: adobeIo
        }))
    }

    const updateDefaultContentful = async (contentful) => {
        setUserData((prev) => ({
            ...prev,
            defaultContentful: contentful
        }))
    }

    const updateDefaultGithub = async (contentful) => {
        setUserData((prev) => ({
            ...prev,
            defaultGithub: contentful
        }))
    }

    const updateDefaultTealiumKey = async (tealiumKey) => {
        setUserData((prev) => ({
            ...prev,
            defaultTealiumKey: tealiumKey
        }))
    }

    const updateDefaultWorkspace = async (workspace) => {
        setUserData((prev) => ({
            ...prev,
            defaultWorkspace: workspace
        }))
    }

    const updateDefaultAEMAdobeIO = async (aemAdobeIO) => {
        try {
            const folders = await getAllAEMAdobeIOFolders(
                userData.token,
                userData.selectedOrganization.org_token,
                aemAdobeIO._id
            );
            let defaultAEMFolder = null
            if (folders?.data?.length > 0) {
                const firstFolder = folders.data[0];
                defaultAEMFolder = firstFolder
            }

            setUserData((prev) => ({
                ...prev,
                defaultAEMAdobeIO: aemAdobeIO,
                defaultAEMFolder: defaultAEMFolder

            }));
        } catch (error) {
            console.error("Failed to fetch AEM Adobe IO folders", error);
        }
    };

    const setSelectedOrganization = async (organization, navigateToDasboard = true) => {
        setLoading(true)
        if (organization?.isBlocked) return
        localStorage.setItem('selectedOrganization', JSON.stringify(organization));
        const allIntegrations = await getAllIntegrations(organization)
        setUserData((prev) => ({
            ...prev,
            selectedOrganization: organization,
            ...allIntegrations
        }))
        reFetchATAudienceConfigRules(organization.org_token)
        setLoading(false)
        if (navigateToDasboard) {
            navigate("/dashboard")
        }
    }

    const isOwnerOfSelectedOrganization = () => {
        const res = userData?.selectedOrganization?.role?.find(item => item.name.toLowerCase().includes("owner"))
        return res ? true : false;
    }

    const checkPermission = (name) => {
        if (!name) return false;
        const isPermission = pathExistsInObject(userData?.selectedOrganization?.permissions, name);
        return isPermission;
    }

    return (
        <UserContext.Provider
            value={{
                roles,
                appData,
                getUserProfileData,
                getAllAppData,
                userData,
                setUserData,
                setSelectedOrganization,
                isOwnerOfSelectedOrganization,
                loading,
                checkPermission,
                reFetchATAudienceConfigRules,
                updateDefaultAdobeIO,
                updateDefaultTealiumKey,
                updateDefaultWorkspace,
                updateDefaultAEMAdobeIO,
                updateDefaultContentful,
                updateDefaultGithub,
                updateAdobeIoIntegrations,
                updateTealiumIntegrations,
                updateWorkspaces,
                updateAEMAdobeIOIntegrations,
                updateContentfulIntegrations,
                updateGithubIntegrations,
                updateSelectedOrgData,
                updateDefaultAEMFolder
            }}
        >
            {children}
        </UserContext.Provider>
    );

}

export const useUserContext = () => useContext(UserContext);
